/**
 * Product card with optional color swatches, media carousel + Quick View.
 *
 * Behavior:
 *   - Swipe / arrow through up to 4 media on the card. Only the first slide
 *     ships with a real src; the rest hydrate from data-src on the first
 *     interaction (hover, touch, arrow, scroll) so grids stay light.
 *   - Click a swatch → swap the whole carousel to that sibling product's
 *     media (from its data-media JSON) + update links, title, price.
 *   - Click [data-quick-view] → dispatch `quick-view:open` on document so the
 *     <quick-view-modal> in the layout opens the product in a popup.
 *
 * The selected variant is whichever variant the active swatch points at, or
 * the default variant from the initial render if no swatches exist.
 */
import { Component } from "@theme/component";

const SLIDE_SIZES = "(min-width: 769px) 25vw, 50vw";

/**
 * Cards that wanted to play and weren't allowed to. iOS refuses autoplay
 * outright in Low Power Mode but will start the same video off any real
 * gesture, so the first tap on the page releases the whole backlog. One
 * listener for every card on the page, armed only once something is waiting.
 */
const blockedVideos = new Set();
let gestureArmed = false;

function playOnFirstGesture(video) {
  blockedVideos.add(video);
  if (gestureArmed) return;
  gestureArmed = true;

  const release = () => {
    for (const el of blockedVideos) {
      el.play().catch(() => {});
    }
    blockedVideos.clear();
  };
  document.addEventListener("touchstart", release, { once: true, passive: true });
  document.addEventListener("click", release, { once: true });
}

class ProductCard extends Component {
  setup() {
    this.addEventListener("click", (e) => this.#onClick(e));
    this.addEventListener("keydown", (e) => this.#onKeydown(e));
    this.#observeCardVideo();
    this.#setupCarousel();
    this.#setupPeek();
  }

  /**
   * Show the second media while the shopper is looking at the card: a mouse
   * resting on it, or a finger held down. Both slide the existing carousel
   * rather than swapping an image, so the dots and the swipe stay in step.
   */
  #setupPeek() {
    const track = this._track;
    if (!track) return;
    if (track.querySelectorAll(".product-card-slide").length < 2) return;

    let peeking = false;

    const slides = track.querySelectorAll(".product-card-slide");
    const second = slides[1] && slides[1].querySelector("img");

    // Slides past the first carry their source in data-src, so scrolling to
    // one the instant it is hydrated lands on an empty box and the card's
    // placeholder colour shows through. Wait for the pixels.
    const ready = () => {
      this.#hydrateSlides();
      if (!second || second.complete) return Promise.resolve();
      return new Promise((resolve) => {
        second.addEventListener("load", resolve, { once: true });
        second.addEventListener("error", resolve, { once: true });
      });
    };

    const to = (index) => {
      track.scrollTo({
        left: index * track.clientWidth,
        behavior: "smooth",
      });
    };

    const show = () => {
      // Only from the first slide: someone who has swiped to slide 3 is
      // reading that one, and yanking them back would be worse than nothing.
      if (peeking || Math.round(track.scrollLeft / track.clientWidth) !== 0)
        return;
      peeking = true;
      ready().then(() => {
        // Long enough to load that the pointer has since left, or a finger
        // has lifted.
        if (peeking) to(1);
      });
    };

    const hide = () => {
      if (!peeking) return;
      peeking = false;
      to(0);
    };

    // The media, not the card: hovering the title or the price is not asking
    // to see another photograph.
    const media = this.querySelector(".product-card-media") || track;
    media.addEventListener("pointerenter", (e) => {
      if (e.pointerType === "mouse") show();
    });
    media.addEventListener("pointerleave", (e) => {
      if (e.pointerType === "mouse") hide();
    });

    // Touch works on two clocks. The image changes as soon as a finger rests
    // — a tap that lingers even slightly is already asking to see more — but
    // the tap only stops opening the product once the press is long enough to
    // read as deliberate. In between, the shopper sees the second photograph
    // and still lands on the product, which is what a tap meant.
    const PEEK_AFTER = 120;
    const HOLD = 350;

    let timer = null;
    let startY = 0;
    let pressedAt = 0;
    const cancel = () => {
      clearTimeout(timer);
      timer = null;
    };

    media.addEventListener(
      "touchstart",
      (e) => {
        startY = e.touches[0].clientY;
        pressedAt = Date.now();
        cancel();
        timer = setTimeout(() => {
          timer = null;
          show();
        }, PEEK_AFTER);
      },
      { passive: true },
    );

    media.addEventListener(
      "touchmove",
      (e) => {
        if (Math.abs(e.touches[0].clientY - startY) > 8) {
          this._peeked = false;
          cancel();
          hide();
        }
      },
      { passive: true },
    );

    ["touchend", "touchcancel"].forEach((type) =>
      media.addEventListener(
        type,
        () => {
          this._peeked = peeking && Date.now() - pressedAt >= HOLD;
          cancel();
          hide();
        },
        { passive: true },
      ),
    );

    // A hold that reached the peek must not also open the product on release.
    this.addEventListener(
      "click",
      (e) => {
        if (!this._peeked) return;
        this._peeked = false;
        e.preventDefault();
        e.stopPropagation();
      },
      true,
    );
  }

  #onKeydown(e) {
    if (e.key !== "Enter" && e.key !== " ") return;
    const swatch = e.target.closest("[data-swatch]");
    if (!swatch) return;
    e.preventDefault();
    this.#applySwatch(swatch);
  }

  #observeCardVideo() {
    const video = this.querySelector("[data-media-video]");
    if (!video || typeof IntersectionObserver === "undefined") return;

    // Set as properties, not just attributes. Phones are the strict ones about
    // autoplay, and a card that arrives through an innerHTML swap — the
    // collection filter does exactly that — can reach play() before the
    // browser has read those attributes off the markup.
    video.muted = true;
    video.playsInline = true;

    // On a phone a card often scrolls into view before its file has enough
    // data, and preload="metadata" means there isn't much yet. play() rejects,
    // and the observer has no reason to fire again, so the card sits on its
    // poster for good — hence the one retry once the data lands.
    const play = () => {
      const started = video.play();
      if (!started) return;
      started.catch(() => {
        video.addEventListener(
          "canplay",
          () => {
            video.play().catch(() => playOnFirstGesture(video));
          },
          { once: true },
        );
        playOnFirstGesture(video);
      });
    };

    this._videoObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            play();
          } else {
            video.pause();
          }
        }
      },
      { threshold: 0.25 },
    );
    this._videoObserver.observe(this);
  }

  #setupCarousel() {
    const track = this.querySelector("[data-card-track]");
    if (!track) return;
    this._track = track;

    // Hydrate the lazy slides the first time the shopper shows intent.
    const hydrate = () => this.#hydrateSlides();
    this.addEventListener("pointerenter", hydrate, { once: true });
    track.addEventListener("touchstart", hydrate, { once: true, passive: true });
    track.addEventListener(
      "scroll",
      () => {
        this.#hydrateSlides();
        this.#syncDots();
      },
      { passive: true },
    );

    this.querySelectorAll("[data-card-arrow]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.#hydrateSlides();
        const dir = btn.dataset.cardArrow === "next" ? 1 : -1;
        track.scrollBy({ left: dir * track.clientWidth, behavior: "smooth" });
      });
    });
  }

  #hydrateSlides() {
    if (this._hydrated || !this._track) return;
    this._hydrated = true;
    this._track.querySelectorAll("img[data-src]").forEach((img) => {
      img.src = img.dataset.src;
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.removeAttribute("data-src");
      img.removeAttribute("data-srcset");
    });
  }

  #syncDots() {
    const track = this._track;
    const dots = this.querySelectorAll("[data-card-dots] span");
    if (!track || !dots.length) return;
    const i = Math.round(track.scrollLeft / track.clientWidth);
    dots.forEach((d, idx) => d.classList.toggle("is-active", idx === i));
  }

  disconnectedCallback() {
    this._videoObserver?.disconnect();
    super.disconnectedCallback?.();
  }

  #onClick(e) {
    const swatch = e.target.closest("[data-swatch]");
    if (swatch) {
      e.preventDefault();
      e.stopPropagation();
      this.#applySwatch(swatch);
      return;
    }

    const quickView = e.target.closest("[data-quick-view]");
    if (quickView) {
      e.preventDefault();
      e.stopPropagation();
      const handle = quickView.dataset.productHandle;
      if (handle) {
        document.dispatchEvent(
          new CustomEvent("quick-view:open", { detail: { handle } }),
        );
      }
    }
  }

  /**
   * Replace the carousel with the sibling product's media. Built with
   * createElement (not an HTML string) so title-derived alt text can't
   * inject markup. Stays on the slide the shopper was already looking at
   * (clamped when the sibling has fewer media), so switching colors feels
   * like the same view changing color, not a reset to the start.
   */
  #swapCarousel(items, alt) {
    const track = this._track;
    const width = track.clientWidth;
    const prevIndex = width ? Math.round(track.scrollLeft / width) : 0;
    const keep = Math.max(0, Math.min(prevIndex, items.length - 1));

    // Swap content inside the existing slides instead of emptying the track:
    // a momentarily-empty scroll-snap container clamps to 0 and the snap
    // logic re-applies that on the next frame, overriding any scrollLeft we
    // set. With the preceding slides untouched, the offset survives on its own.
    const slides = Array.from(track.children);
    items.forEach((m, i) => {
      let slide = slides[i];
      if (!slide) {
        slide = document.createElement("div");
        slide.className = "product-card-slide";
        track.appendChild(slide);
      }
      const img = document.createElement("img");
      img.src = m.s;
      if (m.ss) img.srcset = m.ss;
      img.sizes = SLIDE_SIZES;
      if (i !== keep) img.loading = "lazy";
      img.alt = alt || "";
      img.className = "product-card-slide-img";
      slide.replaceChildren(img);
    });
    while (track.children.length > items.length) {
      track.lastElementChild.remove();
    }
    this._hydrated = true;

    // Only when the sibling has fewer slides than the previous position does
    // the offset need a clamp — after a frame, so it lands post-relayout and
    // the snap logic can't override it.
    if (keep !== prevIndex) {
      requestAnimationFrame(() => {
        track.scrollLeft = keep * track.clientWidth;
      });
    }

    const dotsWrap = this.$("[data-card-dots]");
    if (dotsWrap) {
      dotsWrap.textContent = "";
      items.forEach((_, i) => {
        const dot = document.createElement("span");
        if (i === keep) dot.className = "is-active";
        dotsWrap.appendChild(dot);
      });
      dotsWrap.hidden = items.length < 2;
    }
    this.$$("[data-card-arrow]").forEach((btn) => {
      btn.hidden = items.length < 2;
    });
  }

  #applySwatch(swatch) {
    const d = swatch.dataset;

    let mediaItems = [];
    if (d.media) {
      try {
        mediaItems = JSON.parse(d.media);
      } catch {
        mediaItems = [];
      }
    }

    if (this._track && mediaItems.length) {
      this.#swapCarousel(mediaItems, d.title);
    } else {
      const img = this.$(".product-card-image");
      if (img && d.imageSrc) {
        img.src = d.imageSrc;
        if (d.imageSrcset) img.srcset = d.imageSrcset;
        else img.removeAttribute("srcset");
      }
    }

    // Point both the media and title links at the chosen sibling product.
    if (d.url) {
      this.$$("a").forEach((a) => {
        a.href = d.url;
      });
    }

    const title = this.$(".hhs-card__title");
    if (title && d.title) title.textContent = d.title;

    // Point Quick View at the selected color so its popup shows that product.
    if (d.handle) {
      const quickView = this.$("[data-quick-view]");
      if (quickView) quickView.dataset.productHandle = d.handle;
    }

    // Rebuild the price row so a sale ↔ non-sale swap stays consistent.
    const priceRow = this.$("[data-card-price]");
    if (priceRow && d.price) {
      const onSale = !!d.compare;
      const priceCls = onSale
        ? "text-pink-deep font-bold"
        : "text-ink font-semibold";
      let html = `<span class="${priceCls}">${d.price}</span>`;
      if (onSale) {
        // Mirrors the wrapper in snippets/product-card.liquid: was-price and
        // discount badge together, so the row keeps its two-line mobile shape.
        let was = `<span class="text-fg-soft line-through text-[10px] md:text-[12px] font-normal">${d.compare}</span>`;
        if (d.discount) {
          // Absent unless the sibling is discounted and the theme setting is
          // on, so the attribute alone decides whether the badge renders.
          const style = priceRow.dataset.discountStyle || "";
          was += `<span class="inline-flex items-center px-1.5 py-0.5 bg-pink-deep text-paper font-display text-micro tracking-display uppercase leading-none -rotate-2" style="${style}">\u2212${d.discount}%</span>`;
        }
        html += `<span class="inline-flex items-baseline gap-1.5">${was}</span>`;
      }
      priceRow.innerHTML = html;
    }

    this.$$("[data-swatch]").forEach((s) => s.classList.remove("is-active"));
    swatch.classList.add("is-active");
  }
}

customElements.define("product-card", ProductCard);

export { ProductCard };
